import React from 'react';

import Head from 'next/head';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { getLayout } from 'src/layouts/SiteLayout/StaticPageLayout';
import { PANEL_BILLING } from 'src/const/routes';
import Trackable from 'src/components/common/trackable';
import {
  ArticleCtaButton,
  ArticleImage,
  ArticleParagraph,
  ArticleSmallSubtitle,
  ArticleSubtitle,
  ArticleTitle,
} from 'src/components/common';
import ReactPlayer from 'react-player/lazy';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

const Player = styled(ReactPlayer)`
  margin: 20px auto;
`;

const HowToImproveSeoRank = () => {
  const { t } = useTranslation();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  return (
    <div className="animate__animated animate__fadeIn">
      <Head>
        <title>{t('pages.blog.HowToImproveSeoRank.title')}</title>
        <meta
          name="description"
          content="All of the above rules make up only half of a successful promotion. Every SEO expert knows that for organic growth of positions in search results, your site must first of all be interesting and attract users. But how to achieve this? Before updating your site's rank in the SERP, Google looks at user activity on your site. How? Of course, through the tool that you put to track users on the site - Google analytics."
        />
      </Head>
      <ArticleTitle>{t('pages.blog.HowToImproveSeoRank.articleTitle')}</ArticleTitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.p2')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.p3')}</ArticleParagraph>
      <ArticleSubtitle>🗝️ {t('pages.blog.HowToImproveSeoRank.titleSecret')}</ArticleSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s1p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s1p2')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s1p3')}</ArticleParagraph>
      <ArticleSubtitle>🚀 {t('pages.blog.HowToImproveSeoRank.titleTool')}</ArticleSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s2p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s2p2')}</ArticleParagraph>
      <ArticleSmallSubtitle>📉 {t('pages.blog.HowToImproveSeoRank.subtitleBounceRate')}</ArticleSmallSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s3p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s3p2')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/bounce-tb.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s3p3')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/bounce-ga.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s3p4')}</ArticleParagraph>
      <ArticleSmallSubtitle>📈 {t('pages.blog.HowToImproveSeoRank.subtitleReturningVisitors')}</ArticleSmallSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s4p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s4p2')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/returning-visitors-tb.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s4p3')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/returning-visitors-ga.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s4p4')}</ArticleParagraph>
      <ArticleSmallSubtitle>⏱️ {t('pages.blog.HowToImproveSeoRank.subtitleTimeOnPage')}</ArticleSmallSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s5p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s5p2')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/time-on-page-tb.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s5p3')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/time-on-page-ga.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s5p4')}</ArticleParagraph>
      <ArticleSmallSubtitle>🔎 {t('pages.blog.HowToImproveSeoRank.subtitleOrganicTraffic')}</ArticleSmallSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s6p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s6p2')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/organic-tb.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s6p3')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/organic-ga.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s6p4')}</ArticleParagraph>
      <ArticleSmallSubtitle>📱 {t('pages.blog.HowToImproveSeoRank.subtitleOrganicSocial')}</ArticleSmallSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s7p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s7p2')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/social-tb.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s7p3')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/social-ga.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s7p4')}</ArticleParagraph>
      <ArticleSmallSubtitle>🌏 {t('pages.blog.HowToImproveSeoRank.subtitleGeo')}</ArticleSmallSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s8p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s8p2')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/geo-tb.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s8p3')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/geo-ga.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s8p4')}</ArticleParagraph>
      <ArticleSmallSubtitle>➡️ {t('pages.blog.HowToImproveSeoRank.subtitleCustomerJourney')}</ArticleSmallSubtitle>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s9p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s9p2')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/cj-tb.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s9p3')}</ArticleParagraph>
      <ArticleImage src="/img/blog/how-to-improve-seo-rank/cj-ga.jpg" alt="" />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s9p4')}</ArticleParagraph>
      <ArticleSubtitle>💎 {t('pages.blog.HowToImproveSeoRank.titleSummary')}</ArticleSubtitle>
      <Player
        url="https://www.youtube.com/watch?v=nKwQMi1rpUw"
        controls={true}
        width="auto"
        config={{
          youtube: {
            playerVars: {
              fs: 1,
              playsinline: 0,
              enablejsapi: 1,
              modestbranding: 1,
            },
          },
        }}
        className="col-12 col-lg-8 px-0"
      />
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s10p1')}</ArticleParagraph>
      <ArticleParagraph>{t('pages.blog.HowToImproveSeoRank.s10p2')}</ArticleParagraph>
      <Trackable trackId="blog_howToImproveSeoRank_discount">
        <Link href={isLoggedIn ? PANEL_BILLING : '/#form'} passHref={true}>
          <ArticleCtaButton>
            {t('pages.blog.HowToImproveSeoRank.cta')}{' '}
            <icon className="animate__animated animate__wobble animate__repeat-3 d-inline-block">🎁</icon>
          </ArticleCtaButton>
        </Link>
      </Trackable>
    </div>
  );
};

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
}

HowToImproveSeoRank.getLayout = (page) => getLayout(page);

export default HowToImproveSeoRank;
